import {
  AddMaterialRequisitionApi,
  CancelMaterialRequisitionRemainderApi,
  DeleteMaterialRequisitionApi,
  EditMaterialRequisitionApi,
  GetAllMaterialRequisitionsApi,
  GetMaterialRequisitionByIdApi,
  UpdateMaterialRequisitionApprovalApi,
} from "../../../../Api/Store/MaterialRequisition";

/**
 * Demand Note service: adapter between the original Demand Note screens and the
 * Material Requisition API. Screens keep the original note shape.
 */

const d10 = (v) => (v ? String(v).slice(0, 10) : "");

const toNote = (m) => ({
  Id: m.Id,
  DemandNoteNo: m.MrNumber,
  ProjectId: String(m.ProjectId),
  RequestingSite: m.ProjectName,
  RequisitionDate: d10(m.RequisitionDate),
  ExpectedDeliveryDate: d10(m.RequiredByDate),
  Priority: m.Priority,
  RequestedBy: m.RequestedByName,
  Status: m.Status,
  FirstApprover: m.ApproverNames ?? (m.Approvals || []).map((a) => a.ApproverName).join(", "),
  SpecificInstructions: m.SpecificInstructions || "",
  ProcurementAlert: !!m.ProcurementAlert,
  FileName: m.FileName || "",
  FileUrl: m.FileUrl,
  CreatedAt: m.CreatedAt,
  HasDecision: m.HasDecision,
  CancelledReason: m.CancelledReason,
  Approvals: m.Approvals || [],
  ApproverUserIds: (m.Approvals || []).map((a) => a.ApproverUserId),
  PurchaseRequisitions: m.PurchaseRequisitions || [],
  GatePasses: m.GatePasses || [],
  Items: (m.Lines || []).map((l) => ({
    id: l.Id,
    MaterialId: l.MaterialId,
    MaterialName: l.MaterialName,
    Specification: l.BrandPreference || "",
    MaterialPackagingId: l.MaterialPackagingId || "",
    RequestedPackaging: l.PackagingName || l.BaseUomName || "",
    Unit: l.PackagingName || l.BaseUomName || "",
    EstStock: l.FreeStock != null ? `${l.FreeStock} ${l.BaseUomName}` : "—",
    QtyRequired: l.Quantity,
    UsagePurpose: l.Purpose || "",
    OpenBase: l.OpenBase,
    BaseUomName: l.BaseUomName,
  })),
});

const result = (r, fallback) => ({
  ok: !!r?.data?.IsSuccess,
  message: r?.data?.Message || fallback,
});

export const getDemandNotes = async () => {
  const r = await GetAllMaterialRequisitionsApi();
  return r?.data?.IsSuccess ? (r.data.Data || []).map(toNote) : [];
};

export const getDemandNote = async (id) => {
  const r = await GetMaterialRequisitionByIdApi(id);
  return r?.data?.IsSuccess ? toNote(r.data.Data) : null;
};

// n: the payload AddDemandNote builds, plus File (File|null) and ApproverUserIds.
export const saveDemandNote = async (n) => {
  const fd = new FormData();
  fd.append("ProjectId", n.ProjectId);
  fd.append("Priority", n.Priority);
  fd.append("RequisitionDate", n.RequisitionDate);
  fd.append("RequiredByDate", n.ExpectedDeliveryDate);
  fd.append("SpecificInstructions", n.SpecificInstructions || "");
  fd.append("ProcurementAlert", n.ProcurementAlert ? "true" : "false");
  if (n.File) {
    fd.append("File", n.File);
  }
  (n.Items || []).forEach((it, i) => {
    fd.append(`Lines[${i}].MaterialId`, it.MaterialId);
    if (it.MaterialPackagingId) {
      fd.append(`Lines[${i}].MaterialPackagingId`, it.MaterialPackagingId);
    }
    fd.append(`Lines[${i}].Quantity`, it.QtyRequired);
    fd.append(`Lines[${i}].BrandPreference`, it.Specification || "");
    fd.append(`Lines[${i}].Purpose`, it.UsagePurpose || "");
  });
  (n.ApproverUserIds || []).forEach((id, i) => fd.append(`ApproverUserIds[${i}]`, id));
  const r = n.Id ? await EditMaterialRequisitionApi(n.Id, fd) : await AddMaterialRequisitionApi(fd);
  return result(r, "Failed to save Demand Note");
};

export const deleteDemandNote = async (id) =>
  result(await DeleteMaterialRequisitionApi(id), "Failed to delete demand note");

export const decideDemandNote = async (approvalId, status, comments) =>
  result(
    await UpdateMaterialRequisitionApprovalApi(approvalId, status, comments),
    "Failed to update approval",
  );

// lines: [{ lineId, quantity }]; empty = cancel all remaining.
export const cancelDemandNoteRemainder = async (id, reason, lines) =>
  result(
    await CancelMaterialRequisitionRemainderApi(id, { reason, lines: lines || [] }),
    "Failed to cancel remainder",
  );
